import dayjs from 'dayjs'

export const GROUPING_OPTIONS = [
  { value: 'Account', label: 'Account' },
  { value: 'Account Type', label: 'Account Type' },
  { value: 'Asset', label: 'Asset' },
  { value: 'Asset Category', label: 'Asset Category' },
  { value: 'Asset Class', label: 'Asset Class' },
  { value: 'Custodian', label: 'Custodian' },
  { value: 'Fund Family', label: 'Fund Family' },
  { value: 'Household', label: 'Household' },
  { value: 'Management Style', label: 'Management Style' },
  { value: 'Product', label: 'Product' },
  { value: 'Product Type', label: 'Product Type' },
  { value: 'Registration', label: 'Registration' },
  { value: 'Risk Category', label: 'Risk Category' }
]

// All grouping types that correspond to real Orion CalculationEntity values.
// Used to build entity-specific filter paths (e.g., group-info:Product:Name).
export const ORION_ENTITY_GROUPINGS = new Set([
  'Account', 'Registration', 'Household',
  'Product', 'Asset', 'Representative', 'BrokerDealer'
])

// Only household-owned entities are safe to re-scope directly on expand.
// Firm-wide entities (Product, Asset, Representative, BrokerDealer) must use
// the filtered approach to preserve the household scope.
export const ENTITY_SCOPABLE_GROUPINGS = new Set([
  'Account', 'Registration', 'Household'
])

export const QUICK_DATE_OPTIONS = [
  { value: 'Day', label: 'Day' },
  { value: 'WTD', label: 'WTD' },
  { value: 'MTD', label: 'MTD' },
  { value: 'QTD', label: 'QTD' },
  { value: 'YTD', label: 'YTD' },
  { value: 'One Year', label: '1 Year' },
  { value: 'Two Year', label: '2 Year' },
  { value: 'Three Year', label: '3 Year' },
  { value: 'Five Year', label: '5 Year' },
  { value: 'Ten Year', label: '10 Year' },
  { value: 'Previous 1-Year', label: 'Prev 1 Year' },
  { value: 'Previous 2-Year', label: 'Prev 2 Year' },
  { value: 'Previous 3-Year', label: 'Prev 3 Year' },
  { value: 'Previous 5-Year', label: 'Prev 5 Year' },
  { value: 'Previous 10-Year', label: 'Prev 10 Year' },
  { value: 'Inception', label: 'Inception' },
  { value: 'Holding Period', label: 'Holding Period' }
]

export const DATE_MODE_OPTIONS = [
  { value: 'default', label: 'Default' },
  ...QUICK_DATE_OPTIONS.map(o => ({ value: `qd:${o.value}`, label: o.label })),
  { value: 'dateRange', label: 'Custom Range...' }
]

/**
 * Builds a contextChange date fragment from column options.
 * Returns { quickDate } for presets, { dateRange } for custom ranges, or undefined for default.
 */
export function buildDateContextChange (options) {
  if (!options) return undefined
  const mode = options.dateMode

  if (mode === 'quickDate' && options.quickDate) {
    return { quickDate: options.quickDate }
  }
  if (mode === 'dateRange' && options.startDate && options.endDate) {
    return {
      dateRange: {
        $type: 'date-range',
        startDate: options.startDate,
        endDate: options.endDate
      }
    }
  }
  return undefined
}

/**
 * Builds a column header label that reflects the date configuration.
 */
export function buildDateColumnHeader (fullLabel, shortLabel, options) {
  if (!options) return fullLabel
  const mode = options.dateMode

  if (mode === 'quickDate' && options.quickDate) {
    return `${shortLabel} ${options.quickDate}`
  }
  if (mode === 'dateRange' && options.startDate && options.endDate) {
    const start = dayjs(options.startDate).format('M/D/YY')
    const end = dayjs(options.endDate).format('M/D/YY')
    return `${shortLabel} ${start}-${end}`
  }
  return fullLabel
}

const MV_VALUATION_LABELS = {
  'Ending Market Value': 'Market Value',
  'Beginning Market Value': 'BMV',
  'Ending Bond Accrual': 'Bond Accrual',
  'Beginning Bond Accrual': 'Beg Bond Accrual'
}

function buildMarketValueHeader (opts) {
  return MV_VALUATION_LABELS[opts?.valuationMethod] || 'Market Value'
}

function buildPerfHeader (fullLabel, shortLabel, opts) {
  if (!opts) return fullLabel
  let base = fullLabel
  let short = shortLabel

  const method = opts.calculationMethod
  if (method && method !== 'TWR') {
    base = `${method} ${fullLabel}`
    short = `${method} ${shortLabel}`
  }

  const suffixes = []
  if (opts.annualize) suffixes.push('Ann')
  if (opts.calcGrossOfFees) suffixes.push('Gross')
  if (opts.calcAfterTax) suffixes.push('AT')
  if (suffixes.length) {
    base = `${base} ${suffixes.join(' ')}`
    short = `${short} ${suffixes.join(' ')}`
  }

  return buildDateColumnHeader(base, short, opts)
}

const CB_REALIZED_PREFIX = { Realized: 'Rlzd ' }

function buildCostBasisHeader (fullLabel, opts) {
  if (!opts) return fullLabel
  const prefix = CB_REALIZED_PREFIX[opts.realizationType] || ''
  const suffix = opts.source === 'Custodian' ? ' (Cust)' : ''
  return `${prefix}${fullLabel}${suffix}`
}

const TAX_SAVINGS_LABELS = {
  TaxLossHarvestingSavings: 'TLH Savings',
  TaxTransitionSavings: 'Tax Trans Savings',
  AssetLocationSavings: 'Asset Loc Savings'
}

function buildTaxSavingsHeader (opts) {
  const base = TAX_SAVINGS_LABELS[opts?.taxSavingsValue] || 'Tax Savings'
  return buildDateColumnHeader(base, base, opts)
}

function buildTaxAlphaHeader (opts) {
  const suffix = opts?.taxInceptionType === 'NetDeposits' ? ' (ND)' : ''
  const base = `Tax Alpha${suffix}`
  return buildDateColumnHeader(base, base, opts)
}

const COST_BASIS_SETTINGS = [
  {
    key: 'source',
    label: 'Source',
    control: 'select',
    options: [
      { value: 'Orion', label: 'Orion' },
      { value: 'Custodian', label: 'Custodian' }
    ],
    defaultValue: 'Orion'
  },
  {
    key: 'realizationType',
    label: 'Type',
    control: 'select',
    options: [
      { value: 'Unrealized', label: 'Unrealized' },
      { value: 'Realized', label: 'Realized' }
    ],
    defaultValue: 'Unrealized'
  }
]

const COST_BASIS_DEFAULT_OPTIONS = {
  source: 'Orion',
  realizationType: 'Unrealized'
}

function buildCostBasisContextChange (options) {
  const hasCostBasisSettings = options.source || options.realizationType
  return hasCostBasisSettings
    ? {
      costBasisOption: {
        $type: 'cost-basis',
        ...(options.source && { source: options.source }),
        ...(options.realizationType && { realizationType: options.realizationType })
      }
    }
    : undefined
}

const STATISTIC_TYPE_OPTIONS = [
  { value: 'Standard Deviation', label: 'Standard Deviation' },
  { value: 'Mean', label: 'Mean' },
  { value: 'Variance', label: 'Variance' },
  { value: 'Drawdown', label: 'Drawdown' },
  { value: 'Sharpe Ratio', label: 'Sharpe Ratio' },
  { value: 'Sortino Ratio', label: 'Sortino Ratio' },
  { value: 'Covariance', label: 'Covariance' },
  { value: 'R-Squared', label: 'R-Squared' },
  { value: 'Beta Coefficient', label: 'Beta Coefficient' },
  { value: 'Treynor Ratio', label: 'Treynor Ratio' },
  { value: "Jensen's Alpha", label: "Jensen's Alpha" },
  { value: 'Upside Capture Ratio', label: 'Upside Capture Ratio' },
  { value: 'Downside Capture Ratio', label: 'Downside Capture Ratio' }
]

const STATISTIC_SETTINGS = [
  {
    key: 'statisticType',
    label: 'Statistic',
    control: 'select',
    options: STATISTIC_TYPE_OPTIONS,
    defaultValue: 'Standard Deviation'
  }
]

const PERFORMANCE_DATE_DEFAULTS = {
  dateMode: 'default',
  quickDate: null,
  startDate: null,
  endDate: null
}

const PERFORMANCE_SETTINGS = [
  {
    key: 'calculationMethod',
    label: 'Method',
    control: 'select',
    options: [
      { value: 'TWR', label: 'TWR' },
      { value: 'Simple', label: 'Simple' },
      { value: 'IRR', label: 'IRR' }
    ],
    defaultValue: 'TWR'
  },
  { key: 'annualize', label: 'Annualize', control: 'checkbox', defaultValue: false },
  { key: 'calcGrossOfFees', label: 'Gross of Fees', control: 'checkbox', defaultValue: false },
  { key: 'calcAfterTax', label: 'After Tax', control: 'checkbox', defaultValue: false }
]

function buildPerformanceContextChange (options) {
  const dateContext = buildDateContextChange(options) || {}
  const hasSettings = options.calculationMethod || options.annualize || options.calcGrossOfFees || options.calcAfterTax
  if (hasSettings) {
    dateContext.performanceOption = {
      $type: 'performance',
      ...(options.calculationMethod && { calculationMethod: options.calculationMethod }),
      ...(options.annualize && { annualize: true }),
      ...(options.calcGrossOfFees && { calcGrossOfFees: true }),
      ...(options.calcAfterTax && { calcAfterTax: true })
    }
  }
  return Object.keys(dateContext).length > 0 ? dateContext : undefined
}

function dateKeyPart (opts) {
  return `${opts.dateMode ?? ''}|${opts.quickDate ?? ''}|${opts.startDate ?? ''}|${opts.endDate ?? ''}`
}

function perfKeyPart (opts) {
  return `${dateKeyPart(opts)}|${opts.calculationMethod ?? ''}|${opts.annualize ?? ''}|${opts.calcGrossOfFees ?? ''}|${opts.calcAfterTax ?? ''}`
}

/**
 * Standard activity types Orion supports under the `activity-type` shortcut
 * (also enumerated in activitySummary's `presets.js` ACTIVITY_TYPES). Used
 * as the multiselect option set for the Activity column and Market Value
 * Change derived column.
 */
export const ACTIVITY_TYPE_OPTIONS = [
  { value: 'Contributions', label: 'Contributions' },
  { value: 'Distributions', label: 'Distributions' },
  { value: 'Dividends and Interest', label: 'Dividends and Interest' },
  { value: 'Dividend Interest Reinvested', label: 'Reinvested Income' },
  { value: 'Advisory Fees Paid', label: 'Advisory Fees Paid' },
  { value: 'Miscellaneous Charges', label: 'Miscellaneous Charges' },
  { value: 'Exchange In', label: 'Exchange In' },
  { value: 'Exchange Out', label: 'Exchange Out' },
  { value: 'Journal In', label: 'Journal In' },
  { value: 'Journal Out', label: 'Journal Out' },
  { value: 'Merge In', label: 'Merge In' },
  { value: 'Merge Out', label: 'Merge Out' },
  { value: 'Net Dividends/Interest/Gains Withdrawn', label: 'Net Div/Int/Gains Withdrawn' }
]

const ACTIVITY_TYPE_LABEL_BY_VALUE = Object.fromEntries(
  ACTIVITY_TYPE_OPTIONS.map(o => [o.value, o.label])
)

/**
 * Canonical ordering of activity types by their position in
 * ACTIVITY_TYPE_OPTIONS. Used to keep calcKey, header, and request
 * `details[]` in lock-step regardless of the user's selection order.
 * Two columns with the same set of types in different orders dedup to
 * a single Orion calc, render the same header, and produce identical
 * request shape.
 */
const ACTIVITY_TYPE_ORDER = Object.fromEntries(
  ACTIVITY_TYPE_OPTIONS.map((o, i) => [o.value, i])
)

function sortActivityTypes (types) {
  return [...(types || [])].sort(
    (a, b) => (ACTIVITY_TYPE_ORDER[a] ?? 999) - (ACTIVITY_TYPE_ORDER[b] ?? 999)
  )
}

/**
 * Default activity types contributing to the Market Value Change derived
 * column. Matches activitySummary's "standard" preset (Contributions,
 * Distributions, Advisory Fees, Income = Dividends + Reinvested).
 */
export const MV_CHANGE_DEFAULT_ACTIVITY_TYPES = [
  'Contributions',
  'Distributions',
  'Advisory Fees Paid',
  'Dividends and Interest',
  'Dividend Interest Reinvested'
]

function activityKeyPart (opts) {
  const types = sortActivityTypes(opts?.activityTypes)
  return `${types.join(',')}|${opts?.inceptionValueMethod ?? ''}`
}

function buildActivityHeader (opts) {
  const types = sortActivityTypes(opts?.activityTypes)
  if (types.length === 0) return 'Activity'
  if (types.length === 1) return ACTIVITY_TYPE_LABEL_BY_VALUE[types[0]] ?? types[0]
  if (types.length <= 3) {
    return types.map(t => ACTIVITY_TYPE_LABEL_BY_VALUE[t] ?? t).join(' + ')
  }
  return `Activity (${types.length} types)`
}

function buildMvChangeHeader (opts) {
  return buildDateColumnHeader('Market Value Change', 'MV Change', opts)
}

export const CALC_SOURCES = {
  marketValue: {
    supportsDate: false,
    settingsSchema: [
      {
        key: 'valuationMethod',
        label: 'Valuation',
        control: 'select',
        options: [
          { value: 'Ending Market Value', label: 'Ending Market Value' },
          { value: 'Beginning Market Value', label: 'Beginning Market Value' },
          { value: 'Ending Bond Accrual', label: 'Ending Bond Accrual' },
          { value: 'Beginning Bond Accrual', label: 'Beginning Bond Accrual' }
        ],
        defaultValue: 'Ending Market Value'
      },
      { key: 'includeAccruedInterest', label: 'Include Accrued Interest', control: 'checkbox', defaultValue: true }
    ],
    defaultOptions: { valuationMethod: 'Ending Market Value', includeAccruedInterest: true },
    calcKey: (opts) => `marketValue:${opts.valuationMethod}:${opts.includeAccruedInterest}`,
    buildCalculation: (calcId, options) => ({
      $type: 'activity',
      id: calcId,
      contextChange: {
        activityOption: {
          $type: 'market-value',
          valuationMethod: options.valuationMethod || 'Ending Market Value',
          includeAccruedInterest: options.includeAccruedInterest ?? true
        }
      }
    }),
    fields: {
      primary: { extract: (calc) => calc.value }
    }
  },

  performance: {
    supportsDate: true,
    settingsSchema: PERFORMANCE_SETTINGS,
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      calculationMethod: 'TWR',
      annualize: false,
      calcGrossOfFees: false,
      calcAfterTax: false
    },
    calcKey: (opts) => `performance:${perfKeyPart(opts)}`,
    buildCalculation: (calcId, options) => ({
      $type: 'performance',
      id: calcId,
      contextChange: buildPerformanceContextChange(options)
    }),
    fields: {
      primary: { extract: (calc) => calc.performance }
    },
    benchmarkCounterpart: (opts) => opts?.calcAfterTax ? 'benchmarkAftertaxPerformance' : 'benchmarkPerformance'
  },

  activityType: {
    supportsDate: true,
    settingsSchema: [
      {
        key: 'activityTypes',
        label: 'Activity Types',
        control: 'multiselect',
        options: ACTIVITY_TYPE_OPTIONS,
        defaultValue: ['Contributions']
      },
      {
        key: 'inceptionValueMethod',
        label: 'Inception Value Method',
        control: 'select',
        options: [
          { value: 'New Money', label: 'New Money' },
          { value: 'Zero', label: 'Zero' },
          { value: 'Day After', label: 'Day After' }
        ],
        defaultValue: 'New Money'
      }
    ],
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      activityTypes: ['Contributions'],
      inceptionValueMethod: 'New Money'
    },
    calcKey: (opts) => `activityType:${activityKeyPart(opts)}|${dateKeyPart(opts)}`,
    // Skip emitting an Orion calc when the user has cleared all activity
    // types. resolveCalcAssignments honors this; the column simply has no
    // backing calc and the cell renders as `--`. UI surfaces this via a
    // warning hint under the multiselect (see SettingControl).
    shouldSkip: (opts) => !Array.isArray(opts?.activityTypes) || opts.activityTypes.length === 0,
    buildCalculation: (calcId, options) => {
      // Canonical sort so the request shape, header, and calcKey all
      // agree regardless of the user's selection order.
      const activityTypes = sortActivityTypes(options.activityTypes)
      const inceptionValueMethod = options.inceptionValueMethod || 'New Money'
      return {
        $type: 'activity',
        id: calcId,
        contextChange: {
          ...buildDateContextChange(options),
          // reportCategoryId 2 = "Managed Activity Summary". Mirrors the
          // activitySummary smart component's request shape; without it
          // Orion returns 0 for the activity-type shortcut on this firm.
          reportCategoryId: 2,
          // Verbose activity option shape (rather than the activity-type
          // shortcut). The shortcut is documented but returns 0 in practice
          // for managed-account activity; the longer details[] form with
          // isManaged: true is what activitySummary uses and what works.
          activityOption: {
            $type: 'activity',
            name: buildActivityHeader({ activityTypes }),
            inceptionValueMethod,
            includeUnmanagedJournals: false,
            details: activityTypes.map(activityType => ({
              $type: 'activity',
              activityType,
              isManaged: true,
              flipSign: false
            }))
          }
        }
      }
    },
    fields: {
      primary: { extract: (calc) => calc.value }
    }
  },

  costBasis: {
    supportsDate: false,
    settingsSchema: COST_BASIS_SETTINGS,
    defaultOptions: { ...COST_BASIS_DEFAULT_OPTIONS },
    calcKey: (opts) => `costBasis:${opts.source}:${opts.realizationType}`,
    buildCalculation: (calcId, options) => ({
      $type: 'cost-basis',
      id: calcId,
      contextChange: buildCostBasisContextChange(options)
    }),
    fields: {
      costBasis: { extract: (calc) => calc.costBasis },
      gainLoss: { extract: (calc) => calc.gainLoss }
    }
  },

  estimatedIncome: {
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    calcKey: () => 'estimatedIncome',
    buildCalculation: (calcId) => ({
      $type: 'estimated-annual-income',
      id: calcId
    }),
    fields: {
      primary: { extract: (calc) => calc.estimatedAnnualIncome }
    }
  },

  performanceContribution: {
    supportsDate: true,
    settingsSchema: PERFORMANCE_SETTINGS,
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      calculationMethod: 'TWR',
      annualize: false,
      calcGrossOfFees: false,
      calcAfterTax: false
    },
    calcKey: (opts) => `performanceContribution:${perfKeyPart(opts)}`,
    buildCalculation: (calcId, options) => ({
      $type: 'performance-contribution',
      id: calcId,
      contextChange: buildPerformanceContextChange(options)
    }),
    fields: {
      contribution: { extract: (calc) => calc.contribution },
      avgWeight: { extract: (calc) => calc.averageWeight }
    }
  },

  shorttermCostBasis: {
    supportsDate: false,
    settingsSchema: COST_BASIS_SETTINGS,
    defaultOptions: { ...COST_BASIS_DEFAULT_OPTIONS },
    calcKey: (opts) => `shorttermCostBasis:${opts.source}:${opts.realizationType}`,
    buildCalculation: (calcId, options) => ({
      $type: 'shortterm-cost-basis',
      id: calcId,
      contextChange: buildCostBasisContextChange(options)
    }),
    fields: {
      costBasis: { extract: (calc) => calc.costBasis },
      gainLoss: { extract: (calc) => calc.gainLoss }
    }
  },

  longtermCostBasis: {
    supportsDate: false,
    settingsSchema: COST_BASIS_SETTINGS,
    defaultOptions: { ...COST_BASIS_DEFAULT_OPTIONS },
    calcKey: (opts) => `longtermCostBasis:${opts.source}:${opts.realizationType}`,
    buildCalculation: (calcId, options) => ({
      $type: 'longterm-cost-basis',
      id: calcId,
      contextChange: buildCostBasisContextChange(options)
    }),
    fields: {
      costBasis: { extract: (calc) => calc.costBasis },
      gainLoss: { extract: (calc) => calc.gainLoss }
    }
  },

  benchmarkPerformance: {
    supportsDate: true,
    settingsSchema: PERFORMANCE_SETTINGS,
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      calculationMethod: 'TWR',
      annualize: false,
      calcGrossOfFees: false,
      calcAfterTax: false
    },
    calcKey: (opts) => `benchmarkPerformance:${perfKeyPart(opts)}`,
    buildCalculation: (calcId, options) => ({
      $type: 'benchmark-performance',
      id: calcId,
      contextChange: buildPerformanceContextChange(options)
    }),
    fields: {
      primary: { extract: (calc) => calc.performance }
    }
  },

  benchmarkAftertaxPerformance: {
    supportsDate: true,
    settingsSchema: [],
    defaultOptions: { ...PERFORMANCE_DATE_DEFAULTS },
    calcKey: (opts) => `benchmarkAftertaxPerformance:${dateKeyPart(opts)}`,
    buildCalculation: (calcId, options) => ({
      $type: 'benchmark-aftertax-performance',
      id: calcId,
      contextChange: {
        ...buildDateContextChange(options),
        performanceOption: {
          $type: 'performance',
          calcAfterTax: true
        }
      }
    }),
    fields: {
      primary: { extract: (calc) => calc.performance }
    }
  },

  attributionEffect: {
    supportsDate: true,
    settingsSchema: [],
    defaultOptions: { ...PERFORMANCE_DATE_DEFAULTS },
    calcKey: (opts) => `attributionEffect:${dateKeyPart(opts)}`,
    buildCalculation: (calcId, options) => ({
      $type: 'attribution-effect',
      id: calcId,
      contextChange: buildDateContextChange(options)
    }),
    fields: {
      allocationEffect: { extract: (calc) => calc.attributionEffect?.allocationEffect },
      selectionEffect: { extract: (calc) => calc.attributionEffect?.selectionEffect },
      interactionEffect: { extract: (calc) => calc.attributionEffect?.interactionEffect },
      totalEffect: { extract: (calc) => calc.attributionEffect?.totalEffect }
    }
  },

  yield: {
    supportsDate: false,
    settingsSchema: [
      {
        key: 'yieldType',
        label: 'Yield Type',
        control: 'select',
        options: [
          { value: 'Current', label: 'Current' },
          { value: 'ToMaturityCost', label: 'To Maturity (Cost)' },
          { value: 'ToMaturityMarket', label: 'To Maturity (Market)' },
          { value: 'ToPrerefundedRedemption', label: 'To Prerefunded Redemption' },
          { value: 'ToNextCall', label: 'To Next Call' },
          { value: 'ToWorst', label: 'To Worst' },
          { value: 'OnCost', label: 'On Cost' },
          { value: 'ToBest', label: 'To Best' }
        ],
        defaultValue: 'Current'
      }
    ],
    defaultOptions: { yieldType: 'Current' },
    calcKey: (opts) => `yield:${opts.yieldType}`,
    buildCalculation: (calcId, options) => ({
      $type: 'yield',
      id: calcId,
      ...(options.yieldType && { yieldType: options.yieldType })
    }),
    fields: {
      primary: { extract: (calc) => calc.yield }
    }
  },

  currentYield: {
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    calcKey: () => 'currentYield',
    buildCalculation: (calcId) => ({ $type: 'current-yield', id: calcId }),
    fields: {
      primary: { extract: (calc) => calc.yield }
    }
  },

  yieldOnCost: {
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    calcKey: () => 'yieldOnCost',
    buildCalculation: (calcId) => ({ $type: 'yield-on-cost', id: calcId }),
    fields: {
      primary: { extract: (calc) => calc.yield }
    }
  },

  yieldToMaturity: {
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    calcKey: () => 'yieldToMaturity',
    buildCalculation: (calcId) => ({ $type: 'yield-to-maturity', id: calcId }),
    fields: {
      primary: { extract: (calc) => calc.yield }
    }
  },

  yieldToNextCall: {
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    calcKey: () => 'yieldToNextCall',
    buildCalculation: (calcId) => ({ $type: 'yield-to-next-call', id: calcId }),
    fields: {
      primary: { extract: (calc) => calc.yield }
    }
  },

  yieldToWorst: {
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    calcKey: () => 'yieldToWorst',
    buildCalculation: (calcId) => ({ $type: 'yield-to-worst', id: calcId }),
    fields: {
      primary: { extract: (calc) => calc.yield }
    }
  },

  groupStatistic: {
    supportsDate: true,
    settingsSchema: STATISTIC_SETTINGS,
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      statisticType: 'Standard Deviation'
    },
    calcKey: (opts) => `groupStatistic:${opts.statisticType}|${dateKeyPart(opts)}`,
    buildCalculation: (calcId, options) => ({
      $type: 'group-statistic',
      id: calcId,
      ...(options.statisticType && { statisticType: options.statisticType }),
      contextChange: buildDateContextChange(options)
    }),
    fields: {
      primary: { extract: (calc) => calc.value }
    },
    benchmarkCounterpart: () => 'benchmarkStatistic'
  },

  benchmarkStatistic: {
    supportsDate: true,
    settingsSchema: STATISTIC_SETTINGS,
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      statisticType: 'Standard Deviation'
    },
    calcKey: (opts) => `benchmarkStatistic:${opts.statisticType}|${dateKeyPart(opts)}`,
    buildCalculation: (calcId, options) => ({
      $type: 'benchmark-statistic',
      id: calcId,
      ...(options.statisticType && { statisticType: options.statisticType }),
      contextChange: buildDateContextChange(options)
    }),
    fields: {
      primary: { extract: (calc) => calc.value }
    }
  },

  taxAlpha: {
    supportsDate: true,
    settingsSchema: [
      {
        key: 'taxInceptionType',
        label: 'Inception Type',
        control: 'select',
        options: [
          { value: 'CostBasis', label: 'Cost Basis' },
          { value: 'NetDeposits', label: 'Net Deposits' }
        ],
        defaultValue: 'CostBasis'
      }
    ],
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      taxInceptionType: 'CostBasis'
    },
    calcKey: (opts) => `taxAlpha:${opts.taxInceptionType}|${dateKeyPart(opts)}`,
    buildCalculation: (calcId, options) => ({
      $type: 'tax-alpha',
      id: calcId,
      ...(options.taxInceptionType && { taxInceptionType: options.taxInceptionType }),
      contextChange: buildDateContextChange(options)
    }),
    fields: {
      primary: { extract: (calc) => calc.taxAlpha }
    }
  },

  taxSavings: {
    supportsDate: true,
    settingsSchema: [
      {
        key: 'taxSavingsValue',
        label: 'Savings Type',
        control: 'select',
        options: [
          { value: 'TaxLossHarvestingSavings', label: 'Tax Loss Harvesting' },
          { value: 'TaxTransitionSavings', label: 'Tax Transition' },
          { value: 'AssetLocationSavings', label: 'Asset Location' }
        ],
        defaultValue: 'TaxLossHarvestingSavings'
      }
    ],
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      taxSavingsValue: 'TaxLossHarvestingSavings'
    },
    calcKey: (opts) => `taxSavings:${opts.taxSavingsValue}|${dateKeyPart(opts)}`,
    buildCalculation: (calcId, options) => ({
      $type: 'tax-savings',
      id: calcId,
      ...(options.taxSavingsValue && { taxSavingsValue: options.taxSavingsValue }),
      contextChange: buildDateContextChange(options)
    }),
    fields: {
      primary: { extract: (calc) => calc.taxSavings }
    }
  },

  targetAllocation: {
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    calcKey: () => 'targetAllocation',
    buildCalculation: (calcId) => ({ $type: 'target-allocation', id: calcId }),
    fields: {
      targetWeight: { extract: (calc) => calc.targetWeight },
      actualWeight: { extract: (calc) => calc.actualWeight },
      drift: { extract: (calc) => calc.drift }
    }
  },

  aip: {
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    calcKey: () => 'aip',
    buildCalculation: (calcId) => ({ $type: 'aip', id: calcId }),
    fields: {
      committedCapital: { extract: (calc) => calc.alternativeCommittedCapital },
      contributedCapital: { extract: (calc) => calc.alternativeContributedCapital },
      unfundedCommitment: { extract: (calc) => calc.alternativeUnfundedCommitment },
      unfundedAdj: { extract: (calc) => calc.unfundedCommitmentAdjustment },
      capCall: { extract: (calc) => calc.alternativeCapCallPercent },
      cashDist: { extract: (calc) => calc.alternativeCashDistribution },
      recallDist: { extract: (calc) => calc.alternativeRecallableDistribution }
    }
  }
}

/**
 * Allowed `measureKind` values for {@link COLUMN_CATALOG} entries. Used for tests and validation.
 * @type {readonly ['currency', 'return', 'weight', 'ratio']}
 */
export const MEASURE_KINDS = Object.freeze(['currency', 'return', 'weight', 'ratio'])

/**
 * Semantic measure kind for Portfolio Explorer catalog columns: what **hierarchy / rollup / window**
 * algebra applies (currency-like addition, period returns, weights, unitless ratios). This is **not**
 * the same as each catalog entry's `format`, which drives **display** (e.g. `useFormattingContext`).
 *
 * **Rollup semantics only:** One scalar `measureKind` per row. A column can be `currency` and also
 * be an input to performance or attribution in another layer (e.g. cash flows, net additions); that
 * does **not** get a second “performance” measure kind — linkage to performance is a **separate
 * axis** (future optional fields or composite pipelines), not another value in this enum.
 *
 * See [DEVELOPMENT_NOTES.md](../DEVELOPMENT_NOTES.md) (`measureKind` section) for rationale and examples.
 */
export const COLUMN_CATALOG = [
  // --- Activity ---
  { columnType: 'marketValue', label: 'Market Value', category: 'Activity', calcSource: 'marketValue', field: 'primary', format: 'marketValue', measureKind: 'currency', align: 'right', headerFn: buildMarketValueHeader },
  { columnType: 'estimatedIncome', label: 'Est. Annual Income', category: 'Activity', calcSource: 'estimatedIncome', field: 'primary', format: 'marketValue', measureKind: 'currency', align: 'right' },
  { columnType: 'activity', label: 'Activity', category: 'Activity', calcSource: 'activityType', field: 'primary', format: 'marketValue', measureKind: 'currency', align: 'right', headerFn: buildActivityHeader },

  // --- Performance ---
  { columnType: 'performance', label: 'Performance', category: 'Performance', calcSource: 'performance', field: 'primary', format: 'returns', measureKind: 'return', align: 'right', headerFn: (opts) => buildPerfHeader('Performance', 'Perf', opts) },
  { columnType: 'performanceContribution', label: 'Contribution', category: 'Performance', calcSource: 'performanceContribution', field: 'contribution', format: 'returns', measureKind: 'return', align: 'right', headerFn: (opts) => buildPerfHeader('Contribution', 'Contrib', opts) },
  { columnType: 'performanceContribution:avgWeight', label: 'Avg Weight', category: 'Performance', calcSource: 'performanceContribution', field: 'avgWeight', format: 'returns', measureKind: 'weight', align: 'right', headerFn: (opts) => buildPerfHeader('Avg Weight', 'Avg Wt', opts) },
  { columnType: 'benchmarkPerformance', label: 'Benchmark Perf', category: 'Performance', calcSource: 'benchmarkPerformance', field: 'primary', format: 'returns', measureKind: 'return', align: 'right', headerFn: (opts) => buildPerfHeader('Benchmark Perf', 'BM Perf', opts) },
  { columnType: 'benchmarkAftertaxPerformance', label: 'BM After-Tax Perf', category: 'Performance', calcSource: 'benchmarkAftertaxPerformance', field: 'primary', format: 'returns', measureKind: 'return', align: 'right', headerFn: (opts) => buildDateColumnHeader('BM After-Tax', 'BM AT', opts) },

  // --- Cost Basis ---
  { columnType: 'costBasis', label: 'Cost Basis', category: 'Cost Basis', calcSource: 'costBasis', field: 'costBasis', format: 'marketValue', measureKind: 'currency', align: 'right', headerFn: (opts) => buildCostBasisHeader('Cost Basis', opts) },
  { columnType: 'costBasis:gainLoss', label: 'Gain/Loss', category: 'Cost Basis', calcSource: 'costBasis', field: 'gainLoss', format: 'marketValue', measureKind: 'currency', align: 'right', headerFn: (opts) => buildCostBasisHeader('Gain/Loss', opts) },
  { columnType: 'shorttermCostBasis', label: 'ST Cost Basis', category: 'Cost Basis', calcSource: 'shorttermCostBasis', field: 'costBasis', format: 'marketValue', measureKind: 'currency', align: 'right', headerFn: (opts) => buildCostBasisHeader('ST Cost Basis', opts) },
  { columnType: 'shorttermCostBasis:gainLoss', label: 'ST Gain/Loss', category: 'Cost Basis', calcSource: 'shorttermCostBasis', field: 'gainLoss', format: 'marketValue', measureKind: 'currency', align: 'right', headerFn: (opts) => buildCostBasisHeader('ST Gain/Loss', opts) },
  { columnType: 'longtermCostBasis', label: 'LT Cost Basis', category: 'Cost Basis', calcSource: 'longtermCostBasis', field: 'costBasis', format: 'marketValue', measureKind: 'currency', align: 'right', headerFn: (opts) => buildCostBasisHeader('LT Cost Basis', opts) },
  { columnType: 'longtermCostBasis:gainLoss', label: 'LT Gain/Loss', category: 'Cost Basis', calcSource: 'longtermCostBasis', field: 'gainLoss', format: 'marketValue', measureKind: 'currency', align: 'right', headerFn: (opts) => buildCostBasisHeader('LT Gain/Loss', opts) },

  // --- Attribution ---
  { columnType: 'attributionEffect:allocation', label: 'Allocation Effect', category: 'Attribution', calcSource: 'attributionEffect', field: 'allocationEffect', format: 'returns', measureKind: 'return', align: 'right', headerFn: (opts) => buildDateColumnHeader('Allocation Effect', 'Alloc', opts) },
  { columnType: 'attributionEffect:selection', label: 'Selection Effect', category: 'Attribution', calcSource: 'attributionEffect', field: 'selectionEffect', format: 'returns', measureKind: 'return', align: 'right' },
  { columnType: 'attributionEffect:interaction', label: 'Interaction Effect', category: 'Attribution', calcSource: 'attributionEffect', field: 'interactionEffect', format: 'returns', measureKind: 'return', align: 'right' },
  { columnType: 'attributionEffect:total', label: 'Total Effect', category: 'Attribution', calcSource: 'attributionEffect', field: 'totalEffect', format: 'returns', measureKind: 'return', align: 'right' },

  // --- Yield ---
  { columnType: 'yield', label: 'Yield', category: 'Yield', calcSource: 'yield', field: 'primary', format: 'returns', measureKind: 'return', align: 'right', headerFn: (opts) => `Yield (${opts?.yieldType || 'Current'})` },
  { columnType: 'currentYield', label: 'Current Yield', category: 'Yield', calcSource: 'currentYield', field: 'primary', format: 'returns', measureKind: 'return', align: 'right' },
  { columnType: 'yieldOnCost', label: 'Yield on Cost', category: 'Yield', calcSource: 'yieldOnCost', field: 'primary', format: 'returns', measureKind: 'return', align: 'right' },
  { columnType: 'yieldToMaturity', label: 'YTM', category: 'Yield', calcSource: 'yieldToMaturity', field: 'primary', format: 'returns', measureKind: 'return', align: 'right' },
  { columnType: 'yieldToNextCall', label: 'YTC', category: 'Yield', calcSource: 'yieldToNextCall', field: 'primary', format: 'returns', measureKind: 'return', align: 'right' },
  { columnType: 'yieldToWorst', label: 'YTW', category: 'Yield', calcSource: 'yieldToWorst', field: 'primary', format: 'returns', measureKind: 'return', align: 'right' },

  // --- Statistics ---
  { columnType: 'groupStatistic', label: 'Statistic', category: 'Statistics', calcSource: 'groupStatistic', field: 'primary', format: 'returns', measureKind: 'return', align: 'right', headerFn: (opts) => opts?.statisticType || 'Std Dev' },
  { columnType: 'benchmarkStatistic', label: 'BM Statistic', category: 'Statistics', calcSource: 'benchmarkStatistic', field: 'primary', format: 'returns', measureKind: 'return', align: 'right', headerFn: (opts) => `BM ${opts?.statisticType || 'Std Dev'}` },

  // --- Tax ---
  { columnType: 'taxAlpha', label: 'Tax Alpha', category: 'Tax', calcSource: 'taxAlpha', field: 'primary', format: 'returns', measureKind: 'return', align: 'right', headerFn: buildTaxAlphaHeader },
  { columnType: 'taxSavings', label: 'Tax Savings', category: 'Tax', calcSource: 'taxSavings', field: 'primary', format: 'marketValue', measureKind: 'currency', align: 'right', headerFn: buildTaxSavingsHeader },

  // --- Allocation ---
  { columnType: 'targetAllocation:target', label: 'Target Weight', category: 'Allocation', calcSource: 'targetAllocation', field: 'targetWeight', format: 'returns', measureKind: 'weight', align: 'right' },
  { columnType: 'targetAllocation:actual', label: 'Actual Weight', category: 'Allocation', calcSource: 'targetAllocation', field: 'actualWeight', format: 'returns', measureKind: 'weight', align: 'right' },
  { columnType: 'targetAllocation:drift', label: 'Drift', category: 'Allocation', calcSource: 'targetAllocation', field: 'drift', format: 'returns', measureKind: 'weight', align: 'right' },

  // --- Alternatives ---
  { columnType: 'aip:committedCapital', label: 'Committed Capital', category: 'Alternatives', calcSource: 'aip', field: 'committedCapital', format: 'marketValue', measureKind: 'currency', align: 'right' },
  { columnType: 'aip:contributedCapital', label: 'Contributed Capital', category: 'Alternatives', calcSource: 'aip', field: 'contributedCapital', format: 'marketValue', measureKind: 'currency', align: 'right' },
  { columnType: 'aip:unfundedCommitment', label: 'Unfunded Commitment', category: 'Alternatives', calcSource: 'aip', field: 'unfundedCommitment', format: 'marketValue', measureKind: 'currency', align: 'right' },
  { columnType: 'aip:unfundedAdj', label: 'Unfunded Adj', category: 'Alternatives', calcSource: 'aip', field: 'unfundedAdj', format: 'marketValue', measureKind: 'currency', align: 'right' },
  { columnType: 'aip:capCall', label: 'Cap Call %', category: 'Alternatives', calcSource: 'aip', field: 'capCall', format: 'returns', measureKind: 'return', align: 'right' },
  { columnType: 'aip:cashDist', label: 'Cash Distribution', category: 'Alternatives', calcSource: 'aip', field: 'cashDist', format: 'marketValue', measureKind: 'currency', align: 'right' },
  { columnType: 'aip:recallDist', label: 'Recallable Dist', category: 'Alternatives', calcSource: 'aip', field: 'recallDist', format: 'marketValue', measureKind: 'currency', align: 'right' },

  // --- Activity (derived) — client-computed from bound material columns; see derivedColumns.js ---
  {
    columnType: 'derived:mvChange',
    label: 'Market Value Change',
    category: 'Activity',
    kind: 'derived',
    deriveId: 'mvChange',
    format: 'marketValue',
    measureKind: 'currency',
    align: 'right',
    headerFn: buildMvChangeHeader,
    settingsSchema: [
      {
        key: 'activityTypes',
        label: 'Contributing Activity Types',
        control: 'multiselect',
        options: ACTIVITY_TYPE_OPTIONS,
        defaultValue: MV_CHANGE_DEFAULT_ACTIVITY_TYPES
      }
    ],
    defaultOptions: {
      activityTypes: [...MV_CHANGE_DEFAULT_ACTIVITY_TYPES]
    },
    // mvChange is a "configured" derived column with TWO kinds of ownership:
    //
    //   1) Column-level setting (settingsSchema): `activityTypes`. The user
    //      picks N contributing types; calcInputs fans them out into N
    //      synthetic per-type activity calcs. Overriding `activityTypes` at
    //      the activityType source level would broadcast a single types list
    //      to all N synthetics, collapsing them to one calcKey and breaking
    //      the fan-out.
    //
    //   2) Formula structure (calcInputs optionTemplate): `valuationMethod`
    //      on the marketValue source. The bmv input hardcodes Beginning
    //      Market Value; the emv input hardcodes Ending Market Value. That
    //      distinction is how the formula identifies its two market value
    //      dependencies. Overriding `valuationMethod` at the marketValue
    //      source level would collapse bmv and emv to the same calcKey and
    //      break compute (EMV - (EMV + sum) = -sum).
    //
    // `includeAccruedInterest` (marketValue) and `inceptionValueMethod`
    // (activityType) are safe to override -- they apply consistently across
    // all inputs of their source without collapsing calcKeys.
    //
    // See DEVELOPMENT_NOTES.md section 14 for the inheriting-vs-configured model.
    dependencyOverrides: {
      marketValue: ['includeAccruedInterest'],
      activityType: ['inceptionValueMethod']
    }
  },

  // --- Alternatives (derived) — client-computed from bound material columns; see derivedColumns.js ---
  { columnType: 'derived:dvpi', label: 'DVPI', category: 'Alternatives', kind: 'derived', deriveId: 'dvpi', format: 'returns', measureKind: 'ratio', align: 'right' },
  { columnType: 'derived:moic', label: 'MOIC (TVPI)', category: 'Alternatives', kind: 'derived', deriveId: 'moic', format: 'returns', measureKind: 'ratio', align: 'right' },

  // --- Calculations — cross-row window columns (windowColumns.js) and same-row comparison (comparisonColumns.js) ---
  { columnType: 'window:allocation', label: '% Allocation', category: 'Calculations', kind: 'window', windowType: 'allocation', format: 'returns', measureKind: 'weight', align: 'right' },
  { columnType: 'window:variance', label: 'Variance', category: 'Calculations', kind: 'window', windowType: 'variance', format: 'marketValue', measureKind: 'currency', align: 'right' },
  { columnType: 'window:rank', label: 'Rank', category: 'Calculations', kind: 'window', windowType: 'rank', format: 'integer', measureKind: 'ratio', align: 'right' },
  { columnType: 'window:percentileRank', label: 'Percentile Rank', category: 'Calculations', kind: 'window', windowType: 'percentileRank', format: 'returns', measureKind: 'ratio', align: 'right' },
  { columnType: 'window:concentration', label: 'Concentration', category: 'Calculations', kind: 'window', windowType: 'concentration', format: 'returns', measureKind: 'weight', align: 'right' },
  { columnType: 'comparison', label: 'Comparison', category: 'Calculations', kind: 'comparison', format: null, measureKind: null, align: 'right' }
]

export const COLUMN_CATALOG_MAP = Object.fromEntries(
  COLUMN_CATALOG.map(entry => [entry.columnType, entry])
)

/**
 * Returns the benchmark counterpart calc source name for a given source/options
 * combination, or null if the source has no benchmark equivalent. Used by
 * buildOrionRequest to emit paired benchmark-* calcs inside the Comparison
 * grouping when `includeBenchmarks` is enabled.
 */
export function getBenchmarkCounterpartSource (sourceName, options) {
  const fn = CALC_SOURCES[sourceName]?.benchmarkCounterpart
  if (typeof fn !== 'function') return null
  return fn(options) || null
}

const WINDOW_DEFAULT_OPTIONS = {
  allocation: { sourceColumnType: 'marketValue', sourceColumnId: null, strategy: 'toParent' },
  variance: { sourceColumnType: 'marketValue', sourceColumnId: null, strategy: 'toParent' },
  rank: { sourceColumnType: 'marketValue', sourceColumnId: null, sortDirection: 'desc' },
  percentileRank: { sourceColumnType: 'marketValue', sourceColumnId: null, sortDirection: 'desc' },
  concentration: { sourceColumnType: 'marketValue', sourceColumnId: null }
}

let instanceCounter = 0

export function createColumnInstance (columnType, optionOverrides = {}) {
  const catalog = COLUMN_CATALOG_MAP[columnType]
  if (!catalog) return null
  if (catalog.kind === 'derived') {
    return {
      columnType,
      id: `${columnType}_${instanceCounter++}`,
      label: null,
      options: { dependencyOptions: {}, ...(catalog.defaultOptions || {}), ...optionOverrides }
    }
  }
  if (catalog.kind === 'window') {
    const defaults = WINDOW_DEFAULT_OPTIONS[catalog.windowType] || {}
    return {
      columnType,
      id: `${columnType}_${instanceCounter++}`,
      label: null,
      options: { ...defaults, ...optionOverrides }
    }
  }
  if (catalog.kind === 'comparison') {
    return {
      columnType,
      id: `${columnType}_${instanceCounter++}`,
      label: null,
      options: {
        columnAId: null,
        columnAType: null,
        columnBId: null,
        columnBType: null,
        operation: 'difference',
        ...optionOverrides
      }
    }
  }
  const source = CALC_SOURCES[catalog.calcSource]
  return {
    columnType,
    id: `${columnType}_${instanceCounter++}`,
    label: null,
    options: { ...source.defaultOptions, ...optionOverrides }
  }
}

/** Assigns fresh instance ids for each column. */
export function regenerateColumnIds (columns) {
  if (!columns?.length) return []
  return columns.map(col => ({
    ...col,
    id: `${col.columnType}_${instanceCounter++}`
  }))
}

export const GROUPING_PRESETS = [
  { value: 'account', label: 'By Account', groupings: ['Account'] },
  { value: 'asset-class', label: 'By Asset Class', groupings: ['Asset Class'] },
  { value: 'asset-class-product', label: 'By Asset Class > Product', groupings: ['Asset Class', 'Product'] },
  { value: 'custodian-account', label: 'By Custodian > Account', groupings: ['Custodian', 'Account'] },
  { value: 'account-asset-class', label: 'By Account > Asset Class', groupings: ['Account', 'Asset Class'] },
  { value: 'account-type', label: 'By Account Type', groupings: ['Account Type'] },
  { value: 'custom', label: 'Custom...' }
]

export const DEFAULT_CONFIG = {
  groupings: ['Account'],
  groupingPreset: 'account',
  columns: [
    createColumnInstance('marketValue'),
    createColumnInstance('performance')
  ],
  dateMode: 'quickDate',
  quickDate: 'YTD',
  startDate: null,
  endDate: null,
  fetchAllLevels: false,
  includeBenchmarks: false,
  // SMT-1608 Phase 1: per-grouping scope filters keyed by grouping name.
  // Empty by default; populated by the Groupings & Filters dialog.
  scopeFilters: {}
}
